import { useMemo, useState } from "react";
import { format } from "date-fns";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useDebounce } from "@/hooks/use-debounce";
import { useActiveProgramId } from "./programs/program-scope";
import { apiErrorMessage } from "@/api/perkpoint/client";
import { initialsOf, titleCase } from "@/api/perkpoint/members";
import {
  TXN_CHANNEL_LABELS,
  TransactionsApi,
  txnKeys,
  type ApiTransaction,
  type TxnListQuery,
} from "@/api/perkpoint/transactions";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Label } from "@/components/ui/label";
import { DateRange } from "react-day-picker";
import {
  ArrowDownLeft,
  ArrowUpRight,
  ChevronLeft,
  ChevronRight,
  Coins,
  ExternalLink,
  Eye,
  Gift,
  MoreVertical,
  User,
  Network,
  Search,
} from "lucide-react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DatePickerWithRange,
  perkDatePresetRange,
} from "@/components/ui/date-range-picker";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";

type TxType = "Earn" | "Redeem" | "Adjust" | "Expire" | "Reverse";
type TxStatus = "Completed" | "Pending" | "Failed" | "Reversed";
type QuickTab = "all" | "earn" | "redeem" | "pending" | "failed";

type HistoryRow = {
  id: string;
  txnId: string;
  memberUuid: string;
  memberId: string;
  memberName: string;
  email: string;
  initials: string;
  avatarClass: string;
  type: TxType;
  status: TxStatus;
  points: number;
  description: string;
  channel: string;
  program: string;
  date: string;
  time: string;
  balanceAfter: number | null;
  reference: string;
  outlet: string;
  ruleLabel: string;
  pointsValue: string;
  blockchainTxId: string | null;
  blockchainExplorerUrl: string | null;
};

const QUICK_TAB_FILTER: Record<QuickTab, Pick<TxnListQuery, "type" | "status">> = {
  all: {},
  earn: { type: "earn" },
  redeem: { type: "redeem" },
  pending: { status: "pending" },
  failed: { status: "failed" },
};

function rowFromApi(row: ApiTransaction): HistoryRow {
  const created = new Date(row.created_at);
  const rules = row.applied_rules.filter((rule) => rule.kind !== "validity");
  return {
    id: row.id,
    txnId: row.txn_code,
    memberUuid: row.member.member_id,
    memberId: row.member.external_member_id,
    memberName: row.member.name || "Unnamed member",
    email: row.member.email ?? "",
    initials: initialsOf(row.member.name),
    avatarClass: "bg-emerald-100 text-emerald-800",
    type: titleCase(row.type) as TxType,
    status: titleCase(row.status) as TxStatus,
    points: row.points,
    description: row.description || row.reason || row.txn_code,
    channel: TXN_CHANNEL_LABELS[row.channel] ?? row.channel,
    program: row.program,
    date: created.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }),
    time: created.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" }),
    balanceAfter: row.balance_after,
    reference: row.reference_id ?? "—",
    outlet: row.outlet ?? "",
    ruleLabel: rules.map((rule) => rule.rule_code).join(" + "),
    pointsValue:
      row.points_value != null && row.currency
        ? `${row.currency} ${Math.abs(row.points_value).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`
        : "",
    blockchainTxId: row.blockchain_tx_id,
    blockchainExplorerUrl: row.blockchain_explorer_url,
  };
}

const QUICK_TABS: { id: QuickTab; label: string }[] = [
  { id: "all", label: "All transactions" },
  { id: "earn", label: "Earn" },
  { id: "redeem", label: "Redeem" },
  { id: "pending", label: "Pending" },
  { id: "failed", label: "Failed" },
];

function TypeBadge({ type }: { type: TxType }) {
  const styles: Record<TxType, string> = {
    Earn: "bg-green-50 text-green-700",
    Redeem: "bg-violet-50 text-violet-700",
    Adjust: "bg-amber-50 text-amber-700",
    Expire: "bg-slate-100 text-slate-600",
    Reverse: "bg-orange-50 text-orange-700",
  };
  const icons: Record<TxType, typeof ArrowUpRight> = {
    Earn: ArrowUpRight,
    Redeem: Gift,
    Adjust: Coins,
    Expire: ArrowDownLeft,
    Reverse: ArrowDownLeft,
  };
  const Icon = icons[type];

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium",
        styles[type],
      )}
    >
      <Icon className="h-3.5 w-3.5" />
      {type}
    </span>
  );
}

function StatusBadge({ status }: { status: TxStatus }) {
  const styles: Record<TxStatus, { text: string; dot: string }> = {
    Completed: { text: "text-green-700", dot: "bg-green-500" },
    Pending: { text: "text-amber-700", dot: "bg-amber-500" },
    Failed: { text: "text-red-600", dot: "bg-red-500" },
    Reversed: { text: "text-slate-600", dot: "bg-slate-400" },
  };

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 text-sm",
        styles[status].text,
      )}
    >
      <span className={cn("h-2 w-2 rounded-full", styles[status].dot)} />
      {status}
    </span>
  );
}

function shortHash(hash: string | null) {
  if (!hash) return "Pending";
  if (hash.length <= 16) return hash;
  return `${hash.slice(0, 8)}…${hash.slice(-6)}`;
}

function formatPts(value: number) {
  const abs = Math.abs(value).toLocaleString("en-IN");
  if (value > 0) return `+${abs}`;
  if (value < 0) return `-${abs}`;
  return abs;
}

function DetailRow({
  label,
  value,
  valueClassName,
}: {
  label: string;
  value: string;
  valueClassName?: string;
}) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-slate-100 py-2.5 last:border-b-0">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span
        className={cn(
          "max-w-[60%] text-right text-sm font-medium text-slate-900",
          valueClassName,
        )}
      >
        {value}
      </span>
    </div>
  );
}

function TransactionDetailsModal({
  transaction,
  programId,
  open,
  onOpenChange,
}: {
  transaction: HistoryRow | null;
  programId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const [reverseReason, setReverseReason] = useState("");
  const [reversing, setReversing] = useState(false);
  const detailQuery = useQuery({
    queryKey: txnKeys.detail(programId, transaction?.txnId ?? ""),
    queryFn: () => TransactionsApi.get(programId, transaction!.txnId),
    enabled: open && Boolean(transaction && programId),
  });
  const reverseMutation = useMutation({
    mutationFn: () => TransactionsApi.reverse(programId, transaction!.txnId, reverseReason.trim()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: txnKeys.all });
      queryClient.invalidateQueries({ queryKey: ["perkpoint", "members"] });
      toast.success(`${transaction?.txnId} reversed`);
      setReversing(false);
      setReverseReason("");
    },
    onError: (error) => toast.error(apiErrorMessage(error, "Could not reverse the transaction")),
  });

  if (!transaction) return null;
  const detail = detailQuery.data;
  const appliedRules = detail?.applied_rules ?? [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-slate-900">
            Transaction details
          </DialogTitle>
          <p className="text-sm text-muted-foreground">{transaction.txnId}</p>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
              <p className="text-xs text-muted-foreground">Points</p>
              <p
                className={cn(
                  "mt-1 text-xl font-bold",
                  transaction.points >= 0 ? "text-green-700" : "text-red-600",
                )}
              >
                {formatPts(transaction.points)}
              </p>
              {transaction.pointsValue ? (
                <p className="text-xs text-muted-foreground">Worth {transaction.pointsValue}</p>
              ) : null}
            </div>
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
              <p className="text-xs text-muted-foreground">Status</p>
              <div className="mt-2">
                <StatusBadge status={transaction.status} />
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white px-4 py-1">
            <DetailRow label="Type" value={transaction.type} />
            <DetailRow label="Description" value={transaction.description} />
            <DetailRow label="Member" value={transaction.memberName} />
            <DetailRow label="Member ID" value={transaction.memberId} />
            <DetailRow label="Program" value={transaction.program} />
            <DetailRow label="Channel" value={transaction.channel} />
            <DetailRow label="Outlet" value={transaction.outlet || "—"} />
            <DetailRow
              label="Date & time"
              value={`${transaction.date} · ${transaction.time}`}
            />
            <DetailRow label="Reference" value={transaction.reference} />
            <DetailRow
              label="Balance after"
              value={
                transaction.balanceAfter == null
                  ? "—"
                  : `${transaction.balanceAfter.toLocaleString("en-IN")} pts`
              }
            />
            <DetailRow
              label="Blockchain hash"
              value={transaction.blockchainTxId ? shortHash(transaction.blockchainTxId) : "Pending anchoring"}
            />
          </div>

          {appliedRules.length > 0 ? (
            <div className="rounded-xl border border-green-100 bg-green-50/50 px-4 py-1">
              <p className="pt-3 text-sm font-semibold text-green-800">Rules applied</p>
              {appliedRules.map((rule) => (
                <DetailRow
                  key={`${rule.rule_id}-${rule.kind}`}
                  label={`${rule.rule_code} · ${rule.rule_name}${rule.rule_version ? ` v${rule.rule_version}` : ""}`}
                  value={
                    rule.kind === "validity"
                      ? `${titleCase(rule.validity_type)} · ${rule.days} days`
                      : rule.kind === "multiplier"
                        ? `${rule.multiplier}x · ${formatPts(rule.points ?? 0)}`
                        : formatPts(rule.points ?? 0)
                  }
                />
              ))}
            </div>
          ) : null}

          {detail && transaction.type === "Redeem" ? (
            <div className="rounded-xl border border-violet-100 bg-violet-50/40 px-4 py-1">
              <p className="pt-3 text-sm font-semibold text-violet-800">Redeem details</p>
              <DetailRow label="Reward" value={detail.reward_name || transaction.description} />
              <DetailRow label="Redemption code" value={detail.redemption_code || "—"} />
              <DetailRow
                label="Redemption rule"
                value={detail.redemption_rule ? `${detail.redemption_rule.code} · ${detail.redemption_rule.name}` : "—"}
              />
              {detail.failure_reason ? (
                <DetailRow label="Failure reason" value={detail.failure_reason} valueClassName="text-red-600" />
              ) : null}
            </div>
          ) : null}

          {detail && transaction.type === "Adjust" ? (
            <div className="rounded-xl border border-amber-100 bg-amber-50/40 px-4 py-1">
              <p className="pt-3 text-sm font-semibold text-amber-800">Adjustment details</p>
              <DetailRow label="Adjusted by" value={detail.adjusted_by || "—"} />
              <DetailRow label="Reason" value={detail.adjust_reason || "—"} />
            </div>
          ) : null}

          {detail && transaction.type === "Expire" ? (
            <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-1">
              <p className="pt-3 text-sm font-semibold text-slate-800">Expiry details</p>
              <DetailRow label="Expiry rule" value={detail.expiry_rule || "—"} />
              <DetailRow
                label="Original earn"
                value={
                  [detail.original_txn_code, detail.original_earn_date?.slice(0, 10)]
                    .filter(Boolean)
                    .join(" · ") || "—"
                }
              />
            </div>
          ) : null}

          {detail?.reversals?.length ? (
            <p className="text-sm text-slate-600">
              Reversed by {detail.reversals.map((r) => r.txn_code).join(", ")}.
            </p>
          ) : null}

          {reversing ? (
            <div className="space-y-2 rounded-xl border border-orange-200 bg-orange-50 p-3">
              <Label htmlFor="reverse-reason" className="text-sm font-medium text-orange-900">
                Why reverse this transaction?
              </Label>
              <Input
                id="reverse-reason"
                value={reverseReason}
                onChange={(e) => setReverseReason(e.target.value)}
                placeholder="e.g. Invoice voided"
                className="bg-white"
              />
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" size="sm" className="bg-white" onClick={() => setReversing(false)}>
                  Cancel
                </Button>
                <Button
                  type="button"
                  size="sm"
                  className="bg-orange-600 text-white hover:bg-orange-700"
                  disabled={!reverseReason.trim() || reverseMutation.isPending}
                  onClick={() => reverseMutation.mutate()}
                >
                  Reverse transaction
                </Button>
              </div>
            </div>
          ) : null}
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          {detail?.can_reverse && !reversing ? (
            <Button type="button" variant="outline" className="bg-white text-orange-700" onClick={() => setReversing(true)}>
              Reverse
            </Button>
          ) : null}
          <Button
            type="button"
            variant="outline"
            className="bg-white"
            onClick={() => onOpenChange(false)}
          >
            Close
          </Button>
          <Button
            type="button"
            className="rounded-full bg-[#1B7A45] text-white hover:bg-[#16653A]"
            asChild
          >
            <Link to={`/perkpoint/member/${transaction.memberUuid}`}>View member</Link>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function MemberHistory() {
  const { programId } = useActiveProgramId();
  const [searchParams] = useSearchParams();
  const memberFilter = searchParams.get("member") ?? undefined;
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search.trim(), 300);
  const [quickTab, setQuickTab] = useState<QuickTab>("all");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [channelFilter, setChannelFilter] = useState<string>("all");
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [selectedTransaction, setSelectedTransaction] =
    useState<HistoryRow | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [showBlockchainHash, setShowBlockchainHash] = useState(false);
  const [dateRange, setDateRange] = useState<DateRange | undefined>(
    perkDatePresetRange("last30"),
  );
  const pageSize = 10;

  const tabFilter = QUICK_TAB_FILTER[quickTab];
  const query: TxnListQuery = {
    search: debouncedSearch || undefined,
    type: typeFilter !== "all" ? typeFilter : tabFilter.type,
    status: statusFilter !== "all" ? statusFilter : tabFilter.status,
    channel: channelFilter === "all" ? undefined : channelFilter,
    member_id: memberFilter,
    from: dateRange?.from ? format(dateRange.from, "yyyy-MM-dd") : undefined,
    to: dateRange?.from ? format(dateRange.to ?? dateRange.from, "yyyy-MM-dd") : undefined,
    page,
    pageSize,
  };
  const txnQuery = useQuery({
    queryKey: txnKeys.list(programId ?? "", query),
    queryFn: () => TransactionsApi.list(programId!, query),
    enabled: Boolean(programId),
    placeholderData: keepPreviousData,
  });
  const filteredRows = useMemo(
    () => (txnQuery.data?.items ?? []).map(rowFromApi),
    [txnQuery.data],
  );
  const totalTransactions = txnQuery.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalTransactions / pageSize));

  const openTransactionDetails = (row: HistoryRow) => {
    setSelectedTransaction(row);
    setDetailsOpen(true);
  };

  const allVisibleSelected =
    filteredRows.length > 0 &&
    filteredRows.every((row) => selectedIds.includes(row.id));

  const toggleSelectAll = (checked: boolean) => {
    setSelectedIds(checked ? filteredRows.map((row) => row.id) : []);
  };

  const toggleSelectOne = (id: string, checked: boolean) => {
    setSelectedIds((prev) =>
      checked ? [...prev, id] : prev.filter((value) => value !== id),
    );
  };

  return (
    <div className="mx-auto min-w-0 max-w-6xl space-y-8 pb-4">
      <header className="flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
        <div className="min-w-0">
          <h1 className="text-[1.75rem] font-semibold tracking-tight text-slate-950 sm:text-4xl">
            Member Activities
          </h1>
          <p className="mt-2 max-w-xl text-sm text-slate-500 sm:text-base">
            Earn, redeem, and adjustments for the dates you pick.
          </p>
        </div>
        <DatePickerWithRange
          presets
          date={dateRange}
          setDate={(range) => {
            setDateRange(range);
            setPage(1);
          }}
          className="w-full sm:w-auto [&_button]:h-11 [&_button]:rounded-full [&_button]:bg-white [&_button]:text-slate-700"
        />
      </header>

      {memberFilter ? (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-2.5 text-sm text-emerald-900">
          <span>
            Showing one member&apos;s activity
            {filteredRows[0] ? `: ${filteredRows[0].memberName} (${filteredRows[0].memberId})` : ""}.
          </span>
          <Link to="/perkpoint/member/history" className="font-semibold underline">
            Show all members
          </Link>
        </div>
      ) : null}

      <div className="flex flex-col gap-3 rounded-[24px] bg-white p-3 ring-1 ring-black/[0.06]">
        <div className="relative min-w-0 flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder="Search by member, email, txn ID or description"
            className="h-10 pl-9"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          <Select
            value={typeFilter}
            onValueChange={(value) => {
              setTypeFilter(value);
              setPage(1);
            }}
          >
            <SelectTrigger className="h-10 w-[140px] bg-white">
              <SelectValue placeholder="All types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              <SelectItem value="earn">Earn</SelectItem>
              <SelectItem value="redeem">Redeem</SelectItem>
              <SelectItem value="adjust">Adjust</SelectItem>
              <SelectItem value="expire">Expire</SelectItem>
              <SelectItem value="reverse">Reverse</SelectItem>
            </SelectContent>
          </Select>

          <Select
            value={statusFilter}
            onValueChange={(value) => {
              setStatusFilter(value);
              setPage(1);
            }}
          >
            <SelectTrigger className="h-10 w-[150px] bg-white">
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
              <SelectItem value="reversed">Reversed</SelectItem>
            </SelectContent>
          </Select>

          <Select
            value={channelFilter}
            onValueChange={(value) => {
              setChannelFilter(value);
              setPage(1);
            }}
          >
            <SelectTrigger className="h-10 w-[140px] bg-white">
              <SelectValue placeholder="All channels" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All channels</SelectItem>
              <SelectItem value="in_app">In-app</SelectItem>
              <SelectItem value="pos">POS</SelectItem>
              <SelectItem value="campaign">Campaign</SelectItem>
              <SelectItem value="admin">Admin</SelectItem>
              <SelectItem value="system">System</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {QUICK_TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => {
              setQuickTab(tab.id);
              setPage(1);
            }}
            className={cn(
              "rounded-full border px-3 py-1.5 text-sm font-medium transition-colors",
              quickTab === tab.id
                ? "border-[#1B7A45] bg-emerald-50 text-emerald-900"
                : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50",
            )}
          >
            {tab.label}
          </button>
        ))}
        <label className="ml-auto inline-flex cursor-pointer items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700">
          <Switch
            checked={showBlockchainHash}
            onCheckedChange={setShowBlockchainHash}
            aria-label="Show blockchain hash"
          />
          Show blockchain hash
        </label>
      </div>

      <Card className="rounded-[24px] border-0 bg-white shadow-none ring-1 ring-black/[0.06]">
        <CardContent className="space-y-4 p-4 sm:p-5">
          <div className="overflow-x-auto rounded-lg border border-slate-200">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50/80 hover:bg-slate-50/80">
                  <TableHead className="w-10">
                    <Checkbox
                      checked={allVisibleSelected}
                      onCheckedChange={(checked) =>
                        toggleSelectAll(Boolean(checked))
                      }
                      aria-label="Select all"
                    />
                  </TableHead>
                  <TableHead>Txn ID</TableHead>
                  <TableHead>Member</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Points</TableHead>
                  <TableHead>Channel</TableHead>
                  <TableHead>Status</TableHead>
                  {showBlockchainHash ? (
                    <TableHead>Blockchain hash</TableHead>
                  ) : null}
                  <TableHead>Date</TableHead>
                  <TableHead className="w-12 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRows.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={showBlockchainHash ? 11 : 10}
                      className="py-10 text-center text-sm text-muted-foreground"
                    >
                      {!programId || txnQuery.isLoading
                        ? "Loading transactions…"
                        : txnQuery.isError
                          ? apiErrorMessage(txnQuery.error, "Could not load transactions")
                          : "No transactions match the selected filters."}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredRows.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>
                        <Checkbox
                          checked={selectedIds.includes(row.id)}
                          onCheckedChange={(checked) =>
                            toggleSelectOne(row.id, Boolean(checked))
                          }
                          aria-label={`Select ${row.txnId}`}
                        />
                      </TableCell>
                      <TableCell>
                        <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">
                          {row.txnId}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Avatar className="h-9 w-9">
                            <AvatarFallback
                              className={cn(
                                "text-xs font-semibold",
                                row.avatarClass,
                              )}
                            >
                              {row.initials}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-semibold text-slate-900">
                              {row.memberName}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {row.memberId}
                            </p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <TypeBadge type={row.type} />
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium text-slate-900">
                            {row.description}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {row.ruleLabel ? `Rules ${row.ruleLabel}` : row.program}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span
                          className={cn(
                            "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-sm font-medium",
                            row.points >= 0
                              ? "bg-green-50 text-green-800"
                              : "bg-red-50 text-red-700",
                          )}
                        >
                          <Coins className="h-3.5 w-3.5" />
                          {formatPts(row.points)}
                        </span>
                      </TableCell>
                      <TableCell className="text-slate-700">
                        {row.channel}
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={row.status} />
                      </TableCell>
                      {showBlockchainHash ? (
                        <TableCell>
                          {row.blockchainExplorerUrl ? (
                            <a
                              href={row.blockchainExplorerUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 font-mono text-xs font-medium text-emerald-800 hover:text-emerald-950 hover:underline"
                              title={row.blockchainTxId ?? undefined}
                            >
                              {shortHash(row.blockchainTxId)}
                              <ExternalLink className="h-3.5 w-3.5 shrink-0" />
                            </a>
                          ) : (
                            <span className="text-xs text-muted-foreground">Pending</span>
                          )}
                        </TableCell>
                      ) : null}
                      <TableCell>
                        <div className="text-sm text-slate-700">{row.date}</div>
                        <div className="text-xs text-muted-foreground">
                          {row.time}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                            >
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => openTransactionDetails(row)}
                            >
                              <Eye className="mr-2 h-4 w-4" />
                              View transaction
                            </DropdownMenuItem>
                            {row.blockchainExplorerUrl ? (
                              <DropdownMenuItem asChild>
                                <a
                                  href={row.blockchainExplorerUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  <ExternalLink className="mr-2 h-4 w-4" />
                                  Open blockchain hash
                                </a>
                              </DropdownMenuItem>
                            ) : null}
                            <DropdownMenuItem asChild>
                              <Link to={`/perkpoint/member/${row.memberUuid}`}>
                                <User className="mr-2 h-4 w-4" />
                                View member
                              </Link>
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-muted-foreground">
              {totalTransactions === 0
                ? "No transactions"
                : `Showing ${(page - 1) * pageSize + 1}-${(page - 1) * pageSize + filteredRows.length} of ${totalTransactions.toLocaleString("en-IN")} transactions`}
            </p>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                aria-label="Previous page"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="px-2 text-sm text-slate-700">
                Page {page} of {totalPages.toLocaleString("en-IN")}
              </span>
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                aria-label="Next page"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <TransactionDetailsModal
        transaction={selectedTransaction}
        programId={programId ?? ""}
        open={detailsOpen}
        onOpenChange={setDetailsOpen}
      />
    </div>
  );
}
